'use client';

import type { GetStaticPaths, GetStaticProps } from 'next';
import { Grid2, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AiOutlineWarning } from 'react-icons/ai';
import { Trans } from 'react-i18next';
import { getCorporateconfig } from '@/modules/config';
import { Layout } from '@/ui/Layout';
import { ColumnContainer } from '@/ui/ColumnContainer';
import { NgoHeader } from '@/ui/NgoHeader';
import { Text } from '@/ui/Text';
import { ApiService } from '@/infrastructure/api/ApiService';
import { CampaignResumeShort } from '@/modules/campaign-info/CampaignResumeShort';
import { SeeCampaigns } from '@/modules/campaign-info/SeeCampaigns';
import { CampaignBeneficiaries } from '@/modules/campaign-info/CampaignBeneficiaries';
import { CampaignImage } from '@/modules/campaign-info/CampaignImage';
import { CampaignDescription } from '@/modules/campaign-info/CampaignDescription';
import { transformToLangCode } from '@/infrastructure/zustand/languageStore';
import type { CampaignPageProps } from '@/modules/config.interface';

export const getStaticPaths = (() => {
	return { paths: [], fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({ params }) => {
	const site = params?.site as string;
	const locale = params?.locale as string | undefined;

	const { appConfig } = getCorporateconfig(site);

	const campaignId = appConfig.defaultCampaignId ?? (params?.campaignId as string);

	const langCode = transformToLangCode(locale ?? 'es') ?? 'SPA';

	const corporatesApiService = new ApiService();

	try {
		const [corporate, corporateSeo, exchangeRates] = await Promise.all([
			corporatesApiService.getCorporateCampaign(appConfig.corporateId, campaignId, langCode),
			corporatesApiService.getCorporateSeo(langCode, appConfig.corporateId, campaignId),
			corporatesApiService.getExchangeRates().then((data) => data.exchange),
		]);

		return {
			props: {
				site,
				campaignId,
				clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY ?? '',
				analyticsCode:
					appConfig.analyticsCode === null
						? null
						: process.env.NODE_ENV === 'development'
							? 'G-CPVXDN086T'
							: (appConfig.analyticsCode ?? 'G-XBQHCHY08C'),
				seo: corporateSeo,
				merchantLogo: appConfig.getMerchantLogoUrl?.(campaignId) ?? null,
				metadata: corporate.metadata,
				merchant: corporate.merchant,
				campaign: corporate.campaign,
				aggregate: corporate.aggregate,
				exchangeRates,
				currentLangCode: corporate.langCode,
			},
			revalidate: 300, // In seconds
		};
	} catch (_error) {
		return {
			notFound: true,
		};
	}
}) satisfies GetStaticProps<CampaignPageProps>;

export default function CampaignInfoPage(props: CampaignPageProps) {
	const { campaignInfoConfig, appConfig } = getCorporateconfig(props.site);
	const config = { ...props, ...campaignInfoConfig, page: 'campaign-info' as const };

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const isDownSm = useMediaQuery(theme.breakpoints.down('md'));

	const leftColumnVisible = config.leftColumnVisible?.(isMobile, isDownSm) ?? true;
	const disableClaim = config.disableClaim ?? true;
	const leftColumnBgColor = config.leftColumnBgColor ?? '#eaeaea';

	return (
		// config.merchantLogo has no meaning in campaignInfo and donate pages
		<Layout status="campaign-info" config={{ ...config, merchantLogo: undefined }}>
			<Grid2 container direction="row">
				{leftColumnVisible && (
					<ColumnContainer
						withPadding={false}
						containerStyle={{ backgroundColor: leftColumnBgColor }}
						height={config.isAlala && isTablet && !isDesktop ? 'auto' : undefined}
					>
						<CampaignResumeShort isCampaignInfoPage config={config} appConfig={appConfig} />
					</ColumnContainer>
				)}
				<ColumnContainer
					withPadding={false}
					containerStyle={{ backgroundColor: 'white' }}
					height={config.isAlala && isTablet && !isDesktop ? 'auto' : undefined}
				>
					{(config.campaign.textsNotFound || config.metadata.textsCollection.textsNotFound) && (
						<Grid2
							size={{ xs: 12 }}
							style={{
								textAlign: 'center',
								padding: '20px',
								backgroundColor: theme.palette.primary.main,
							}}
						>
							<Typography color="textSecondary">
								<AiOutlineWarning />
								<Trans i18nKey="unavailableLanguageMessage" />
							</Typography>
						</Grid2>
					)}
					<Grid2
						size={{ xs: 12 }}
						style={{
							textAlign: 'center',
							padding: '0.44rem 0',
							display: `${isMobile || isTablet ? 'none' : 'block'}`,
							backgroundColor: 'white',
						}}
					>
						<SeeCampaigns />
					</Grid2>
					<NgoHeader
						config={config}
						customCampaignByText={config.customCampaignByText}
						customCountries={config.customCountries?.(config.campaign.id)}
					/>
					<Grid2 size={{ xs: 12 }} style={{ marginBottom: '100px' }}>
						<Grid2 container style={{ padding: '0  5% 25px 5%' }}>
							<Grid2 size={{ xs: 12 }}>
								{!disableClaim && (
									<Text sx={{ fontSize: '18px', textAlign: 'left' }} text={config.campaign.texts.claim} />
								)}
							</Grid2>
							{!config.disableCampaignBeneficiaries?.(config.campaign.id) && (
								<CampaignBeneficiaries
									config={config}
									formatBeneficiariesAsSpanishNumber={config.formatBeneficiariesAsSpanishNumber}
									isAlala={config.isAlala}
								/>
							)}{' '}
							<CampaignImage image={config.campaign.media ?? ''} />
							<CampaignDescription config={config} />
						</Grid2>
					</Grid2>
				</ColumnContainer>
			</Grid2>
		</Layout>
	);
}
